import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Send, AlertTriangle, Info, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { EditableTable, type Column, type RowState } from "../components/EditableTable.js";
import ClaveSiiModal, { type SiiCredentials } from "../components/ClaveSiiModal.js";
import EmisorSelectorModal, { type EmisorInfo } from "../components/EmisorSelectorModal.js";
import ConfirmEmitModal, { type EmitSummaryItem, type ConfirmEmitOptions, type EmailTargetItem } from "../components/ConfirmEmitModal.js";
import ClienteSelectorModal, { type ClienteFrecuenteItem } from "../components/ClienteSelectorModal.js";
import { todayChile } from "../../lib/chileTime.js";
import { validarRut, formatRut } from "../../lib/rut.ts";
import { sanitizeBheText } from "../../lib/siiSanitize.js";
import { reportClientError } from "../sentry.js";

interface FacturaStatus {
  facturaReady: boolean;
  notaCreditoReady: boolean;
  issues: string[];
}

// ── Row types per tab ──

interface BheRow {
  rutEmisor: string;
  tipoRetencion: string;
  fecha: string;
  receptorRut: string;
  receptorNombre: string;
  correo?: string;
  direccion: string;
  receptorRegion: string;
  comuna: string;
  descripcion: string;
  monto: string;
}

interface BeRow {
  rutEmisor: string;
  receptorRut: string;
  receptorNombre: string;
  direccion: string;
  telefono: string;
  correo: string;
  monto: string;
  detalle: string;
}

interface FacturaRow {
  rutEmisor: string;
  receptorRut: string;
  receptorNombre: string;
  receptorGiro: string;
  receptorDireccion: string;
  receptorComuna: string;
  monto: string;
  fecha: string;
  descripcion: string;
  exenta?: string;
}

interface NcRow {
  rutEmisor: string;
  folioRef: string;
  tipoRef: string;
  monto: string;
  fecha: string;
  razon: string;
}

const emptyBhe = (): BheRow => ({ rutEmisor: "", tipoRetencion: "2", fecha: todayChile(), receptorRut: "", receptorNombre: "", correo: "", direccion: "", receptorRegion: "", comuna: "", descripcion: "", monto: "" });
const emptyBe = (): BeRow => ({ rutEmisor: "", receptorRut: "", receptorNombre: "", direccion: "", telefono: "", correo: "", monto: "", detalle: "" });
const emptyFactura = (): FacturaRow => ({ rutEmisor: "", receptorRut: "", receptorNombre: "", receptorGiro: "", receptorDireccion: "", receptorComuna: "", monto: "", fecha: todayChile(), descripcion: "" });
const emptyNc = (): NcRow => ({ rutEmisor: "", folioRef: "", tipoRef: "33", monto: "", fecha: todayChile(), razon: "Anula documento" });

const bheColumns: Column<BheRow>[] = [
  { key: "rutEmisor", label: "RUT Emisor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut", highlight: true },
  { key: "tipoRetencion", label: "Retención", type: "select", required: true, width: "110px", help: "Receptor: la empresa retiene el PPM. Usar cuando boleteas para una empresa (ella hace la retención).\n\nEmisor: tú retienes y pagas el PPM mensual en el F29. Típico para profesionales independientes.", options: [
    { value: "1", label: "Receptor" },
    { value: "2", label: "Emisor" },
  ] },
  { key: "fecha", label: "Fecha", type: "date", required: true, width: "120px" },
  { key: "receptorRut", label: "RUT Receptor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut" },
  { key: "receptorNombre", label: "Nombre", placeholder: "Razón social o nombre (opcional)", width: "180px" },
  { key: "direccion", label: "Dirección", required: true, width: "150px" },
  { key: "receptorRegion", label: "Región", type: "select", required: true, width: "160px", dynamicOptions: "region", placeholder: "Seleccionar región..." },
  { key: "comuna", label: "Comuna", type: "select", required: true, width: "150px", dynamicOptions: "comuna", placeholder: "Seleccionar comuna..." },
  { key: "descripcion", label: "Detalle", required: true, width: "200px" },
  { key: "monto", label: "Valor", type: "number", required: true, placeholder: "0", width: "100px" },
  { key: "correo", label: "Enviar por correo", placeholder: "cliente@email.com (opcional)", width: "180px", help: "Opcional. El SII enviará automáticamente una copia oficial de la boleta a este correo al emitir. Este correo no queda impreso en el documento tributario." },
];

const beColumns: Column<BeRow>[] = [
  { key: "rutEmisor", label: "RUT Emisor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut", highlight: true },
  { key: "receptorRut", label: "RUT Receptor", placeholder: "66666666-6 (opcional)", width: "120px", validate: "rut" },
  { key: "receptorNombre", label: "Nombre", placeholder: "Nombre receptor (opcional)", width: "160px" },
  { key: "direccion", label: "Dirección", placeholder: "Dirección (opcional)", width: "150px" },
  { key: "telefono", label: "Teléfono", placeholder: "+569... (opcional)", width: "120px" },
  { key: "monto", label: "Monto", type: "number", required: true, placeholder: "0", width: "100px" },
  { key: "detalle", label: "Detalle", placeholder: "Descripción o ítem (opcional)", width: "180px" },
  { key: "correo", label: "Enviar por correo", placeholder: "cliente@email.com (opcional)", width: "180px", help: "Opcional. El SII enviará automáticamente la boleta a este correo al emitir." },
];

const getFacturaColumns = (modo: "exenta" | "afecta"): Column<FacturaRow>[] => [
  { key: "rutEmisor", label: "RUT Emisor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut", highlight: true },
  { key: "receptorRut", label: "RUT Receptor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut" },
  { key: "receptorNombre", label: "Razón Social", required: true, width: "180px" },
  { key: "receptorGiro", label: "Giro", required: true, width: "150px" },
  { key: "receptorDireccion", label: "Dirección", required: true, width: "150px" },
  { key: "receptorComuna", label: "Comuna", required: true, width: "120px" },
  { key: "monto", label: modo === "afecta" ? "Total (IVA inc)" : "Monto", type: "number", required: true, placeholder: "0", width: "115px", help: modo === "afecta" ? "Monto total con 19% de IVA incluido. El sistema desglosa automáticamente Neto e IVA ante el SII." : "Monto total exento de IVA." },
  { key: "fecha", label: "Fecha", type: "date", required: true, width: "120px" },
  { key: "descripcion", label: "Descripción", width: "180px" },
];

const ncColumns: Column<NcRow>[] = [
  { key: "rutEmisor", label: "RUT Emisor", required: true, placeholder: "12345678-9", width: "120px", validate: "rut", highlight: true },
  { key: "folioRef", label: "Folio Ref", type: "number", required: true, placeholder: "123", width: "100px" },
  { key: "tipoRef", label: "Tipo Ref", required: true, width: "90px" },
  { key: "monto", label: "Monto", type: "number", required: true, placeholder: "0", width: "100px" },
  { key: "fecha", label: "Fecha", type: "date", required: true, width: "120px" },
  { key: "razon", label: "Razón", required: true, width: "200px" },
];

type TabType = "bhe" | "be" | "factura" | "nc";

function hasRowData(row: Record<string, unknown>, tab: TabType): boolean {
  if (tab === "be") {
    const r = row as unknown as BeRow;
    return !!(
      r.rutEmisor?.trim() ||
      r.receptorRut?.trim() ||
      r.receptorNombre?.trim() ||
      r.direccion?.trim() ||
      r.telefono?.trim() ||
      r.correo?.trim() ||
      r.monto?.trim() ||
      r.detalle?.trim()
    );
  }
  if (tab === "bhe") {
    const r = row as unknown as BheRow;
    return !!(
      r.rutEmisor?.trim() ||
      r.receptorRut?.trim() ||
      r.receptorNombre?.trim() ||
      r.correo?.trim() ||
      r.direccion?.trim() ||
      r.comuna?.trim() ||
      r.descripcion?.trim() ||
      r.monto?.trim()
    );
  }
  if (tab === "factura") {
    const r = row as unknown as FacturaRow;
    return !!(
      r.rutEmisor?.trim() ||
      r.receptorRut?.trim() ||
      r.receptorNombre?.trim() ||
      r.receptorGiro?.trim() ||
      r.receptorDireccion?.trim() ||
      r.receptorComuna?.trim() ||
      r.monto?.trim() ||
      r.descripcion?.trim()
    );
  }
  if (tab === "nc") {
    const r = row as unknown as NcRow;
    return !!(
      r.rutEmisor?.trim() ||
      r.folioRef?.trim() ||
      r.monto?.trim() ||
      (r.razon?.trim() && r.razon !== "Anula documento")
    );
  }
  return false;
}

interface RowValidationResult {
  error: string;
  fieldKey?: string;
}

function validateRowDetailWithField(row: Record<string, unknown>, tab: TabType): RowValidationResult | null {
  const rutEmisor = String(row.rutEmisor ?? "").trim();
  if (!rutEmisor) return { error: "Falta RUT Emisor", fieldKey: "rutEmisor" };
  if (!validarRut(rutEmisor)) return { error: "RUT Emisor inválido", fieldKey: "rutEmisor" };

  const montoRaw = String(row.monto ?? "").trim();
  if (!montoRaw) return { error: "Falta Monto", fieldKey: "monto" };
  const montoNum = parseInt(montoRaw, 10);
  if (isNaN(montoNum) || montoNum <= 0) return { error: "Monto debe ser > 0", fieldKey: "monto" };

  const normEmisor = rutEmisor.replace(/[.\s-]/g, "").toUpperCase();
  const receptorRutRaw = String(row.receptorRut ?? "").trim();
  const normReceptor = receptorRutRaw.replace(/[.\s-]/g, "").toUpperCase();

  const fechaRaw = String(row.fecha ?? "").trim();
  if (fechaRaw && fechaRaw > todayChile()) {
    return { error: "La fecha no puede ser futura", fieldKey: "fecha" };
  }

  if (tab === "be") {
    const r = row as unknown as BeRow;
    const tieneReceptor = !!receptorRutRaw && normReceptor !== "66666666-6";
    const receptorNombre = String(r.receptorNombre ?? "").trim();

    if (tieneReceptor) {
      if (!validarRut(receptorRutRaw)) return { error: "RUT Receptor inválido", fieldKey: "receptorRut" };
      if (!receptorNombre) return { error: "Falta Nombre receptor", fieldKey: "receptorNombre" };
    } else if (receptorNombre && !receptorRutRaw) {
      return { error: "Falta RUT de receptor", fieldKey: "receptorRut" };
    }
    return null;
  }

  if (tab === "bhe") {
    const r = row as unknown as BheRow;
    const emisorNum = parseInt(normEmisor.slice(0, -1), 10);
    if (!isNaN(emisorNum) && emisorNum > 50000000) {
      return { error: "Empresas (1ª cat) no pueden emitir honorarios", fieldKey: "rutEmisor" };
    }
    if (!receptorRutRaw) return { error: "Falta RUT Receptor", fieldKey: "receptorRut" };
    if (normReceptor === "66666666-6") return { error: "RUT 66.666.666-6 no permitido en BHE", fieldKey: "receptorRut" };
    if (!validarRut(receptorRutRaw)) return { error: "RUT Receptor inválido", fieldKey: "receptorRut" };
    if (normEmisor && normReceptor && normEmisor === normReceptor) {
      return { error: "Emisor y receptor no pueden ser iguales en BHE", fieldKey: "receptorRut" };
    }
    if (!String(r.fecha ?? "").trim()) return { error: "Falta Fecha", fieldKey: "fecha" };
    if (!String(r.direccion ?? "").trim()) return { error: "Falta Dirección", fieldKey: "direccion" };
    if (!String(r.comuna ?? "").trim()) return { error: "Falta Comuna", fieldKey: "comuna" };
    if (!String(r.descripcion ?? "").trim()) return { error: "Falta Detalle", fieldKey: "descripcion" };
    return null;
  }

  if (tab === "factura") {
    const r = row as unknown as FacturaRow;
    if (!receptorRutRaw) return { error: "Falta RUT Receptor", fieldKey: "receptorRut" };
    if (normReceptor === "66666666-6") return { error: "RUT 66.666.666-6 no permitido en Factura", fieldKey: "receptorRut" };
    if (!validarRut(receptorRutRaw)) return { error: "RUT Receptor inválido", fieldKey: "receptorRut" };
    if (normEmisor && normReceptor && normEmisor === normReceptor) {
      return { error: "Emisor y receptor no pueden ser iguales en Factura", fieldKey: "receptorRut" };
    }
    if (!String(r.receptorNombre ?? "").trim()) return { error: "Falta Razón Social", fieldKey: "receptorNombre" };
    if (!String(r.receptorGiro ?? "").trim()) return { error: "Falta Giro", fieldKey: "receptorGiro" };
    if (!String(r.receptorDireccion ?? "").trim()) return { error: "Falta Dirección", fieldKey: "receptorDireccion" };
    if (!String(r.receptorComuna ?? "").trim()) return { error: "Falta Comuna", fieldKey: "receptorComuna" };
    if (!String(r.fecha ?? "").trim()) return { error: "Falta Fecha", fieldKey: "fecha" };
    if (!String(r.descripcion ?? "").trim()) return { error: "Falta Descripción", fieldKey: "descripcion" };
    return null;
  }

  if (tab === "nc") {
    const r = row as unknown as NcRow;
    const folioRef = parseInt(String(r.folioRef ?? "").trim(), 10);
    if (!folioRef || isNaN(folioRef) || folioRef <= 0) return { error: "Falta Folio Ref", fieldKey: "folioRef" };
    if (!String(r.tipoRef ?? "").trim()) return { error: "Falta Tipo Ref", fieldKey: "tipoRef" };
    if (!String(r.fecha ?? "").trim()) return { error: "Falta Fecha", fieldKey: "fecha" };
    if (!String(r.razon ?? "").trim()) return { error: "Falta Razón", fieldKey: "razon" };
    return null;
  }

  return null;
}

function validateRowDetail(row: Record<string, unknown>, tab: TabType): string | null {
  return validateRowDetailWithField(row, tab)?.error ?? null;
}

function scrollToCell(rowIdx: number, fieldKey?: string) {
  setTimeout(() => {
    let targetCell: HTMLElement | null = null;
    if (fieldKey) {
      targetCell =
        document.querySelector<HTMLElement>(`[data-state="active"] td[data-row="${rowIdx}"][data-col-key="${fieldKey}"]`) ||
        document.querySelector<HTMLElement>(`td[data-row="${rowIdx}"][data-col-key="${fieldKey}"]`);
    }
    if (!targetCell) {
      targetCell =
        document.querySelector<HTMLElement>(`[data-state="active"] tr[data-row="${rowIdx}"]`) ||
        document.querySelector<HTMLElement>(`tr[data-row="${rowIdx}"]`);
    }
    if (targetCell) {
      targetCell.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });

      targetCell.classList.add("ring-2", "ring-destructive", "ring-offset-1", "bg-destructive/20");
      setTimeout(() => {
        targetCell?.classList.remove("ring-2", "ring-destructive", "ring-offset-1", "bg-destructive/20");
      }, 3000);

      const focusable = targetCell.querySelector<HTMLElement>("input, select, textarea, button");
      if (focusable) {
        focusable.focus();
      } else {
        targetCell.dispatchEvent(new MouseEvent("dblclick", { bubbles: true, cancelable: true }));
      }
    }
  }, 100);
}

function isRowValid<T extends Record<string, unknown>>(row: T, cols: Column<T>[]): boolean {
  if ("receptorRut" in row && "receptorNombre" in row) {
    const rut = String(row.receptorRut ?? "").trim();
    const nombre = String(row.receptorNombre ?? "").trim();
    if (rut && rut !== "66666666-6" && !nombre) return false;
  }
  return cols.every((c) => {
    if (c.required && String(row[c.key] ?? "").trim() === "") return false;
    if (c.validate === "rut") {
      const val = String(row[c.key] ?? "").trim();
      if (val && !validarRut(val)) return false;
    }
    return true;
  });
}

function validateTableBeforeEmit<T extends Record<string, unknown>>(
  rows: T[],
  states: RowState[],
  setStates: (s: RowState[]) => void,
  tab: TabType,
): number[] | null {
  const indicesWithData = rows
    .map((_, i) => i)
    .filter((i) => hasRowData(rows[i], tab));

  if (indicesWithData.length === 0) {
    if (rows.length > 0) {
      const firstRes = validateRowDetailWithField(rows[0], tab);
      const firstErr = firstRes?.error ?? "Fila incompleta";
      const nextStates = [...states];
      while (nextStates.length < rows.length) nextStates.push({ status: "idle" });
      nextStates[0] = { status: "error", error: firstErr };
      setStates(nextStates);
      if (firstRes?.fieldKey) {
        scrollToCell(0, firstRes.fieldKey);
      }
    }
    toast.error("Ingresa los datos en al menos una fila antes de emitir.");
    return null;
  }

  const nextStates = [...states];
  while (nextStates.length < rows.length) nextStates.push({ status: "idle" });

  let errorCount = 0;
  let firstErrorRow: number | null = null;
  let firstErrorField: string | null = null;

  for (const i of indicesWithData) {
    const res = validateRowDetailWithField(rows[i], tab);
    if (res) {
      nextStates[i] = { status: "error", error: res.error };
      if (firstErrorRow === null) {
        firstErrorRow = i;
        firstErrorField = res.fieldKey ?? null;
      }
      errorCount++;
    } else if (nextStates[i]?.status === "error") {
      nextStates[i] = { status: "idle" };
    }
  }

  if (errorCount > 0) {
    setStates(nextStates);
    if (firstErrorRow !== null) {
      scrollToCell(firstErrorRow, firstErrorField ?? undefined);
    }
    const firstMsg = firstErrorRow !== null ? nextStates[firstErrorRow]?.error : null;
    const msgPrefix = firstMsg && firstErrorRow !== null ? `Fila ${firstErrorRow + 1}: ${firstMsg}. ` : "";
    toast.error(
      `${msgPrefix}Hay ${errorCount} fila(s) con datos incompletos. Revisa las columnas marcadas.`,
      { duration: 5000 },
    );
    return null;
  }

  return indicesWithData;
}

export default function EmitirPage() {
  const [facturaStatus, setFacturaStatus] = useState<FacturaStatus | null>(null);
  const [emitting, setEmitting] = useState(false);
  const [claveModalOpen, setClaveModalOpen] = useState(false);
  const [modalRequiresCert, setModalRequiresCert] = useState(false);
  const [pendingEmit, setPendingEmit] = useState<(() => (creds: SiiCredentials) => void) | null>(null);
  const [emisorModalOpen, setEmisorModalOpen] = useState(false);
  const [emisores, setEmisores] = useState<EmisorInfo[]>([]);
  const [emisoresLoading, setEmisoresLoading] = useState(false);
  const [pendingBeEmit, setPendingBeEmit] = useState<{
    creds: SiiCredentials;
    indices: number[];
    options?: ConfirmEmitOptions;
  } | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState<EmitSummaryItem | null>(null);
  const [pendingConfirmAction, setPendingConfirmAction] = useState<((options: ConfirmEmitOptions) => void) | null>(null);

  // Row data per tab
  const [bheRows, setBheRows] = useState<BheRow[]>([emptyBhe()]);
  const [bheStates, setBheStates] = useState<RowState[]>([]);
  const [beRows, setBeRows] = useState<BeRow[]>([emptyBe()]);
  const [beStates, setBeStates] = useState<RowState[]>([]);
  const [facturaRows, setFacturaRows] = useState<FacturaRow[]>([emptyFactura()]);
  const [facturaStates, setFacturaStates] = useState<RowState[]>([]);
  const [ncRows, setNcRows] = useState<NcRow[]>([emptyNc()]);
  const [ncStates, setNcStates] = useState<RowState[]>([]);

  // Factura modo y directorio de clientes
  const [facturaModo, setFacturaModo] = useState<"exenta" | "afecta">("exenta");
  const [clientes, setClientes] = useState<ClienteFrecuenteItem[]>([]);
  const [clienteSelectorOpen, setClienteSelectorOpen] = useState(false);
  const [activeSelectorTab, setActiveSelectorTab] = useState<TabType>("factura");

  const loadClientes = useCallback(() => {
    api<{ clientes: ClienteFrecuenteItem[] }>("/clientes")
      .then((res) => {
        if (Array.isArray(res.clientes)) setClientes(res.clientes);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  const computedFacturaColumns = useMemo(
    () => getFacturaColumns(facturaModo),
    [facturaModo]
  );

  useEffect(() => {
    api<FacturaStatus>("/factura/status").then(setFacturaStatus).catch(() => {});
  }, []);

  // ── Pre-flight checks ──

  const facturaReady = facturaStatus?.facturaReady ?? true;
  const facturaIssues = facturaStatus?.issues ?? [];
  const ncReady = facturaStatus?.notaCreditoReady ?? true;

  const renderBeModosInfo = () => (
    <div className="mb-3 rounded-lg border border-border/70 bg-secondary/20 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <Info className="h-4 w-4 text-primary" />
          4 Modos de emisión detectados automáticamente
        </span>
        <span className="text-[11px] text-muted-foreground hidden sm:inline">
          El modo se infiere según los campos que completes en cada fila
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="rounded-md border border-border/50 bg-background/80 p-2.5 space-y-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
            ⚡ Solo Monto
          </span>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Solo rellena <strong>Monto</strong>. Emisión ultra rápida para venta al paso (receptor anónimo).
          </p>
        </div>
        <div className="rounded-md border border-border/50 bg-background/80 p-2.5 space-y-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-400">
            📝 Con Detalle
          </span>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Rellena <strong>Monto + Detalle</strong>. Venta anónima pero con descripción del producto o servicio.
          </p>
        </div>
        <div className="rounded-md border border-border/50 bg-background/80 p-2.5 space-y-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400">
            👤 Nominativa
          </span>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Rellena <strong>RUT y Nombre</strong> del cliente (+ Dirección, Teléfono, Correo opcionales).
          </p>
        </div>
        <div className="rounded-md border border-border/50 bg-background/80 p-2.5 space-y-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-500/15 text-purple-600 dark:text-purple-400">
            🌟 Completa
          </span>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Rellena <strong>Receptor (RUT + Nombre) + Detalle + Monto</strong>. La boleta nominativa más completa.
          </p>
        </div>
      </div>
    </div>
  );

  const renderPreflight = (ready: boolean, issues: string[]) => {
    if (ready && issues.length === 0) return (
      <div className="mb-3 rounded-md bg-secondary/30 border border-secondary/40 px-3 py-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Info className="h-3.5 w-3.5 shrink-0 text-secondary-foreground" />
          <span className="text-xs font-semibold text-secondary-foreground">Antes de emitir</span>
        </div>
        <ul className="text-xs text-muted-foreground space-y-1 pl-5">
          <li>Pega desde Excel (Ctrl+V) o sube un CSV con tus datos</li>
          <li><strong className="text-destructive">RUT Emisor es crítico</strong>: es el RUT desde el cual se emitirán las boletas. Asegúrate de completarlo correctamente.</li>
          <li>Los campos marcados con <span className="text-destructive">*</span> son obligatorios</li>
        </ul>
      </div>
    );
    return (
      <Alert variant="warning" className="mb-3">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Prerequisitos pendientes</AlertTitle>
        <AlertDescription>
          <ul className="text-sm space-y-0.5">
            {issues.map((issue, i) => <li key={i}>• {issue}</li>)}
          </ul>
        </AlertDescription>
      </Alert>
    );
  };

  // ── Mass emission ──

  async function emitBatch<T extends Record<string, unknown>>(
    rows: T[],
    states: RowState[],
    setStates: (s: RowState[]) => void,
    cols: Column<T>[],
    buildPayload: (row: T, creds: SiiCredentials, index: number) => Record<string, unknown>,
    endpoint: string,
    creds: SiiCredentials,
    targetIndices?: number[],
  ) {
    const validIndices = targetIndices ?? rows.map((_, i) => i).filter((i) => isRowValid(rows[i], cols));
    if (validIndices.length === 0) { toast.error("No hay filas válidas para emitir"); return; }

    setEmitting(true);
    const newStates = [...states];
    let ok = 0;
    let fail = 0;

    const TERMINAL_STATES = ["EMITIDO", "EMITIDO_SIN_PDF", "EMITIDO_SIN_HTML", "ACEPTADO", "RECHAZADO", "ERROR", "ANULADO"];

    for (const idx of validIndices) {
      newStates[idx] = { status: "emitting" };
      setStates([...newStates]);
      try {
        const payload = buildPayload(rows[idx], creds, idx);
        const res = await api<Record<string, unknown>>(endpoint, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: { "Idempotency-Key": crypto.randomUUID() },
        });

        if (res.success && res.documentoId && res.estado === "QUEUED") {
          // 202 received — poll until terminal state
          const documentoId = res.documentoId as number;
          let estado = "QUEUED";
          let folio: number | undefined;
          let errorMensaje: string | undefined;
          let attempts = 0;
          const MAX_POLL_ATTEMPTS = 120; // 120 * 2s = 4min max

          while (!TERMINAL_STATES.includes(estado) && attempts < MAX_POLL_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, 2000));
            const doc = await api<{ estado: string; folio: number; errorMensaje?: string }>(`/documentos/${documentoId}`);
            estado = doc.estado;
            folio = doc.folio;
            errorMensaje = doc.errorMensaje ?? undefined;
            attempts++;
          }

          if (estado === "EMITIDO" || estado === "EMITIDO_SIN_PDF" || estado === "EMITIDO_SIN_HTML" || estado === "ACEPTADO") {
            ok++;
            newStates[idx] = { status: "success", folio };
            if (estado === "EMITIDO_SIN_PDF") {
              toast.warning(`Boleta ${folio} emitida. PDF oficial no disponible, se usó versión alternativa.`);
            } else if (estado === "EMITIDO_SIN_HTML") {
              toast.warning(`Boleta ${folio} emitida. Descarga no disponible — busca tu boleta en el portal del SII.`);
            }
          } else {
            fail++;
            const errMsg = errorMensaje ?? `Estado: ${estado}`;
            console.error("[Emitir] Documento terminó en estado no exitoso:", estado, errMsg);
            reportClientError(new Error(errMsg), { flow: "emitBatch", endpoint, rowIndex: idx, estado, documentoId });
            newStates[idx] = { status: "error", error: errMsg };
          }
        } else if (res.success) {
          ok++;
          newStates[idx] = { status: "success", folio: typeof res.folio === "number" ? res.folio : undefined };
        } else {
          fail++;
          const msg = typeof res.mensaje === "string" ? res.mensaje : "Error desconocido";
          console.error("[Emitir] Respuesta no exitosa:", msg, res);
          reportClientError(new Error(msg), { flow: "emitBatch", endpoint, rowIndex: idx, response: res });
          newStates[idx] = { status: "error", error: msg };
        }
      } catch (err) {
        fail++;
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[Emitir] Error en fila", idx, ":", msg, err);
        reportClientError(err, { flow: "emitBatch", endpoint, rowIndex: idx, message: msg });
        newStates[idx] = { status: "error", error: msg };
        if (msg.includes("SII temporalmente no disponible")) {
          toast.error("El SII está bloqueando nuestras solicitudes. Tus datos no se perderán. Intenta más tarde.");
          setStates([...newStates]);
          setEmitting(false);
          return;
        }
      }
      setStates([...newStates]);
    }

    setEmitting(false);
    if (ok > 0) {
      toast.success(`${ok} documento(s) emitidos`);
      loadClientes(); // Refrescar clientes aprendidos en background
    }
    if (fail > 0) toast.error(`${fail} documento(s) fallidos`);
  }

  const requestEmit = useCallback(
    (fn: (creds: SiiCredentials) => void, requiresCertificado = false) => {
      setPendingEmit(() => fn);
      setModalRequiresCert(requiresCertificado);
      setClaveModalOpen(true);
    },
    [],
  );

  const handleClaveSubmit = useCallback(
    (creds: SiiCredentials) => {
      setClaveModalOpen(false);
      if (pendingEmit) {
        pendingEmit(creds);
        setPendingEmit(null);
      }
    },
    [pendingEmit],
  );

  // ── BHE emission ──
  const proceedEmitBhe = (validIndices: number[], options?: ConfirmEmitOptions) => {
    requestEmit((creds) =>
      emitBatch(
        bheRows,
        bheStates,
        setBheStates,
        bheColumns,
        (row, c, idx) => {
          const shouldSendEmail = !!(
            options?.sendSiiEmail &&
            row.correo?.trim() &&
            !options.excludedEmailIndices?.includes(idx)
          );
          return {
            rutEmisor: row.rutEmisor,
            rutSii: c.rutSii,
            claveSii: c.claveSii,
            receptorRut: row.receptorRut,
            receptorNombre: sanitizeBheText(row.receptorNombre).cleaned,
            receptorCorreo: row.correo?.trim() || undefined,
            enviarSiiEmail: shouldSendEmail,
            monto: parseInt(row.monto) || 0,
            descripcionServicio: sanitizeBheText(row.descripcion).cleaned || undefined,
            fechaServicio: row.fecha,
            receptorDireccion: sanitizeBheText(row.direccion).cleaned || undefined,
            receptorComuna: row.comuna || undefined,
            receptorRegion: row.receptorRegion || undefined,
            tipoRetencion: row.tipoRetencion === "1" ? 1 : 2,
          };
        },
        "/bhe/emitir",
        creds,
        validIndices,
      ),
    );
  };

  const emitBhe = () => {
    const validIndices = validateTableBeforeEmit(bheRows, bheStates, setBheStates, "bhe");
    if (!validIndices) return;

    let totalMonto = 0;
    const emisoresSet = new Set<string>();
    const emailTargets: EmailTargetItem[] = [];

    for (const idx of validIndices) {
      const row = bheRows[idx];
      totalMonto += parseInt(row.monto) || 0;
      if (row.rutEmisor.trim()) emisoresSet.add(row.rutEmisor.trim());
      if (row.correo && row.correo.trim()) {
        emailTargets.push({
          idx,
          receptorNombre: row.receptorNombre?.trim(),
          receptorRut: row.receptorRut?.trim(),
          correo: row.correo.trim(),
        });
      }
    }

    setConfirmSummary({
      tab: "bhe",
      documentName: "Boletas de Honorarios Electrónicas",
      totalCount: validIndices.length,
      totalMonto,
      distinctEmisores: Array.from(emisoresSet),
      emailTargets,
    });
    setPendingConfirmAction(() => (options: ConfirmEmitOptions) => proceedEmitBhe(validIndices, options));
    setConfirmModalOpen(true);
  };

  // ── BE emission ──
  const proceedEmitBe = (validIndices: number[], options?: ConfirmEmitOptions) => {
    requestEmit((creds) => {
      setPendingBeEmit({ creds, indices: validIndices, options });
      setEmisoresLoading(true);
      setEmisorModalOpen(true);
      api<{ emisores: EmisorInfo[] }>("/be/emisores", {
        method: "POST",
        body: JSON.stringify({ rutSii: creds.rutSii, claveSii: creds.claveSii }),
      })
        .then((res) => {
          setEmisores(res.emisores ?? []);
          setEmisoresLoading(false);
        })
        .catch((err) => {
          toast.error("No se pudieron obtener las empresas disponibles: " + (err instanceof Error ? err.message : String(err)));
          setEmisorModalOpen(false);
          setEmisoresLoading(false);
        });
    });
  };

  const emitBe = () => {
    const validIndices = validateTableBeforeEmit(beRows, beStates, setBeStates, "be");
    if (!validIndices) return;

    let totalMonto = 0;
    let sin_nada = 0;
    let solo_glosa = 0;
    let receptor_sin_detalle = 0;
    let completa = 0;
    const emisoresSet = new Set<string>();
    const emailTargets: EmailTargetItem[] = [];

    for (const idx of validIndices) {
      const row = beRows[idx];
      totalMonto += parseInt(row.monto) || 0;
      if (row.rutEmisor.trim()) emisoresSet.add(row.rutEmisor.trim());
      const tieneReceptor = !!row.receptorRut.trim() && row.receptorRut !== "66666666-6";
      const tieneDetalle = !!row.detalle.trim();
      if (tieneReceptor && tieneDetalle) completa++;
      else if (tieneReceptor) receptor_sin_detalle++;
      else if (tieneDetalle) solo_glosa++;
      else sin_nada++;

      if (row.correo && row.correo.trim()) {
        emailTargets.push({
          idx,
          receptorNombre: row.receptorNombre?.trim(),
          receptorRut: row.receptorRut?.trim(),
          correo: row.correo.trim(),
        });
      }
    }

    setConfirmSummary({
      tab: "be",
      documentName: "Boletas Electrónicas",
      totalCount: validIndices.length,
      totalMonto,
      distinctEmisores: Array.from(emisoresSet),
      beBreakdown: { sin_nada, solo_glosa, receptor_sin_detalle, completa },
      emailTargets,
    });
    setPendingConfirmAction(() => (options: ConfirmEmitOptions) => proceedEmitBe(validIndices, options));
    setConfirmModalOpen(true);
  };

  const handleEmisorSelect = useCallback(
    (emisorRut: string) => {
      setEmisorModalOpen(false);
      if (pendingBeEmit) {
        const { creds, indices, options } = pendingBeEmit;
        emitBatch(
          beRows,
          beStates,
          setBeStates,
          beColumns,
          (row, c, idx) => {
            const tieneReceptor = !!row.receptorRut.trim() && row.receptorRut !== "66666666-6";
            const tieneDetalle = !!row.detalle.trim();
            const modo = tieneReceptor && tieneDetalle ? "completa" : tieneReceptor ? "receptor_sin_detalle" : tieneDetalle ? "solo_glosa" : "sin_nada";
            const shouldSendEmail = !!(
              options?.sendSiiEmail &&
              row.correo?.trim() &&
              !options.excludedEmailIndices?.includes(idx)
            );
            return {
              rutEmisor: row.rutEmisor,
              rutSii: c.rutSii,
              claveSii: c.claveSii,
              emisorRut,
              modo,
              receptorRut: row.receptorRut || undefined,
              receptorNombre: row.receptorNombre || undefined,
              receptorDireccion: row.direccion || undefined,
              receptorTelefono: row.telefono || undefined,
              receptorCorreo: row.correo?.trim() || undefined,
              enviarSiiEmail: shouldSendEmail,
              monto: parseInt(row.monto) || 0,
              detalle: row.detalle || undefined,
              descripcionServicio: row.detalle || undefined,
            };
          },
          "/be/emitir",
          creds,
          indices,
        );
        setPendingBeEmit(null);
      }
    },
    [pendingBeEmit, beRows, beStates, beColumns],
  );

  // ── Factura emission ──
  const proceedEmitFactura = (validIndices: number[]) => {
    requestEmit(
      (creds) =>
        emitBatch(
          facturaRows,
          facturaStates,
          setFacturaStates,
          computedFacturaColumns,
          (row, c) => ({
            rutEmisor: row.rutEmisor,
            rutSii: c.rutSii,
            claveSii: c.claveSii,
            claveCertificado: c.claveCertificado,
            receptorRut: row.receptorRut,
            receptorNombre: row.receptorNombre,
            receptorGiro: row.receptorGiro,
            receptorDireccion: row.receptorDireccion,
            receptorComuna: row.receptorComuna,
            monto: parseInt(row.monto) || 0,
            descripcionServicio: row.descripcion,
            fechaServicio: row.fecha,
            exento: facturaModo === "exenta",
          }),
          "/factura/emitir",
          creds,
          validIndices,
        ),
      true,
    );
  };

  const emitFactura = () => {
    const validIndices = validateTableBeforeEmit(facturaRows, facturaStates, setFacturaStates, "factura");
    if (!validIndices) return;

    let totalMonto = 0;
    const emisoresSet = new Set<string>();
    for (const idx of validIndices) {
      const row = facturaRows[idx];
      totalMonto += parseInt(row.monto) || 0;
      if (row.rutEmisor.trim()) emisoresSet.add(row.rutEmisor.trim());
    }

    setConfirmSummary({
      tab: "factura",
      documentName: facturaModo === "exenta" ? "Facturas Exentas (DTE 34)" : "Facturas Afectas con IVA (DTE 33)",
      totalCount: validIndices.length,
      totalMonto,
      distinctEmisores: Array.from(emisoresSet),
    });
    setPendingConfirmAction(() => () => proceedEmitFactura(validIndices));
    setConfirmModalOpen(true);
  };

  // ── Handlers de Selección de Clientes ──
  const openClienteSelectorForTab = (tab: TabType) => {
    setActiveSelectorTab(tab);
    setClienteSelectorOpen(true);
  };

  const getActiveTabEmisor = (): string | undefined => {
    let targetRows: Record<string, unknown>[] = [];
    if (activeSelectorTab === "factura") targetRows = facturaRows;
    else if (activeSelectorTab === "bhe") targetRows = bheRows;
    else if (activeSelectorTab === "be") targetRows = beRows;

    const withEmisor = targetRows.find((r) => String(r.rutEmisor ?? "").trim());
    return withEmisor ? String(withEmisor.rutEmisor).trim() : undefined;
  };

  const handleSelectClienteFromModal = (cliente: ClienteFrecuenteItem) => {
    if (activeSelectorTab === "factura") {
      const newRow: FacturaRow = {
        rutEmisor: formatRut(cliente.rutEmisor),
        receptorRut: formatRut(cliente.rut),
        receptorNombre: cliente.razonSocial,
        receptorGiro: cliente.giro ?? "",
        receptorDireccion: cliente.direccion ?? "",
        receptorComuna: cliente.comuna ?? "",
        monto: "",
        fecha: todayChile(),
        descripcion: cliente.glosaHabitual ?? "Servicios profesionales",
      };
      setFacturaRows([...facturaRows, newRow]);
      setFacturaStates([...facturaStates, { status: "idle" }]);
    } else if (activeSelectorTab === "bhe") {
      const isEmpresa = parseInt(cliente.rut.replace(/[.\s-]/g, ""), 10) > 50000000;
      const newRow: BheRow = {
        rutEmisor: formatRut(cliente.rutEmisor),
        tipoRetencion: isEmpresa ? "1" : "2",
        fecha: todayChile(),
        receptorRut: formatRut(cliente.rut),
        receptorNombre: cliente.razonSocial,
        correo: cliente.email ?? "",
        direccion: cliente.direccion ?? "",
        comuna: cliente.comuna ?? "",
        receptorRegion: "",
        descripcion: cliente.glosaHabitual ?? "Servicios profesionales",
        monto: "",
      };
      setBheRows([...bheRows, newRow]);
      setBheStates([...bheStates, { status: "idle" }]);
    } else if (activeSelectorTab === "be") {
      const newRow: BeRow = {
        rutEmisor: formatRut(cliente.rutEmisor),
        receptorRut: formatRut(cliente.rut),
        receptorNombre: cliente.razonSocial,
        direccion: cliente.direccion ?? "",
        telefono: cliente.telefono ?? "",
        correo: cliente.email ?? "",
        monto: "",
        detalle: cliente.glosaHabitual ?? "Venta",
      };
      setBeRows([...beRows, newRow]);
      setBeStates([...beStates, { status: "idle" }]);
    }
  };

  const handleSelectClienteInRow = (rowIdx: number, cliente: ClienteFrecuenteItem, tab: TabType) => {
    if (tab === "factura") {
      const updated = [...facturaRows];
      if (updated[rowIdx]) {
        updated[rowIdx] = {
          ...updated[rowIdx],
          receptorRut: formatRut(cliente.rut),
          receptorNombre: cliente.razonSocial,
          receptorGiro: cliente.giro ?? updated[rowIdx].receptorGiro,
          receptorDireccion: cliente.direccion ?? updated[rowIdx].receptorDireccion,
          receptorComuna: cliente.comuna ?? updated[rowIdx].receptorComuna,
          descripcion: updated[rowIdx].descripcion || cliente.glosaHabitual || "",
        };
        setFacturaRows(updated);
      }
    } else if (tab === "bhe") {
      const updated = [...bheRows];
      if (updated[rowIdx]) {
        const isEmpresa = parseInt(cliente.rut.replace(/[.\s-]/g, ""), 10) > 50000000;
        updated[rowIdx] = {
          ...updated[rowIdx],
          receptorRut: formatRut(cliente.rut),
          receptorNombre: cliente.razonSocial,
          correo: cliente.email ?? updated[rowIdx].correo,
          direccion: cliente.direccion ?? updated[rowIdx].direccion,
          comuna: cliente.comuna ?? updated[rowIdx].comuna,
          tipoRetencion: isEmpresa ? "1" : "2",
          descripcion: updated[rowIdx].descripcion || cliente.glosaHabitual || "",
        };
        setBheRows(updated);
      }
    } else if (tab === "be") {
      const updated = [...beRows];
      if (updated[rowIdx]) {
        updated[rowIdx] = {
          ...updated[rowIdx],
          receptorRut: formatRut(cliente.rut),
          receptorNombre: cliente.razonSocial,
          direccion: cliente.direccion ?? updated[rowIdx].direccion,
          correo: cliente.email ?? updated[rowIdx].correo,
          telefono: cliente.telefono ?? updated[rowIdx].telefono,
          detalle: updated[rowIdx].detalle || cliente.glosaHabitual || "",
        };
        setBeRows(updated);
      }
    }
  };

  // ── Nota de Crédito emission ──
  const proceedEmitNc = (validIndices: number[]) => {
    requestEmit(
      (creds) =>
        emitBatch(
          ncRows,
          ncStates,
          setNcStates,
          ncColumns,
          (row, c) => ({
            rutEmisor: row.rutEmisor,
            rutSii: c.rutSii,
            claveSii: c.claveSii,
            claveCertificado: c.claveCertificado,
            folioReferencia: parseInt(row.folioRef) || 0,
            tipoReferencia: parseInt(row.tipoRef) || 33,
            monto: parseInt(row.monto) || 0,
            descripcionServicio: row.razon,
            fechaServicio: row.fecha,
          }),
          "/factura/nota-credito",
          creds,
          validIndices,
        ),
      true,
    );
  };

  const emitNc = () => {
    const validIndices = validateTableBeforeEmit(ncRows, ncStates, setNcStates, "nc");
    if (!validIndices) return;

    let totalMonto = 0;
    const emisoresSet = new Set<string>();
    for (const idx of validIndices) {
      const row = ncRows[idx];
      totalMonto += parseInt(row.monto) || 0;
      if (row.rutEmisor.trim()) emisoresSet.add(row.rutEmisor.trim());
    }

    setConfirmSummary({
      tab: "nc",
      documentName: "Notas de Crédito",
      totalCount: validIndices.length,
      totalMonto,
      distinctEmisores: Array.from(emisoresSet),
    });
    setPendingConfirmAction(() => () => proceedEmitNc(validIndices));
    setConfirmModalOpen(true);
  };

  const renderEmitButton = (
    rows: Record<string, unknown>[],
    tab: TabType,
    ready: boolean,
    onClick: () => void,
    label: string,
  ) => {
    const countWithData = rows.filter((r) => hasRowData(r, tab)).length;
    const countDisplay = countWithData > 0 ? countWithData : rows.length;

    return (
      <div className="flex items-center gap-3 mt-3">
        <Button variant="cta" onClick={onClick} disabled={!ready || emitting || rows.length === 0}>
          {emitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Send className="mr-2 h-4 w-4" />
          {label} ({countDisplay})
        </Button>
        {!ready && <span className="text-sm text-muted-foreground">Completa los prerequisitos para habilitar</span>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="bhe">
        <TabsList>
          <TabsTrigger value="bhe">BHE</TabsTrigger>
          <TabsTrigger value="be">Boleta Electrónica</TabsTrigger>
          <TabsTrigger value="factura">Factura</TabsTrigger>
          <TabsTrigger value="nota-credito">Nota de Crédito</TabsTrigger>
        </TabsList>

        {/* BHE Tab */}
        <TabsContent value="bhe" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Boleta Honorarios Electrónica</CardTitle>
              <CardDescription>Pega filas desde Excel o sube CSV. Emisión masiva secuencial.</CardDescription>
            </CardHeader>
            <CardContent>
              {renderPreflight(true, [])}
              <EditableTable
                columns={bheColumns}
                rows={bheRows}
                onChange={setBheRows}
                rowStates={bheStates}
                onRowStatesChange={setBheStates}
                emptyRow={emptyBhe}
                validateRow={(r) => validateRowDetail(r, "bhe")}
                onOpenClienteSelector={() => openClienteSelectorForTab("bhe")}
                clientes={clientes}
                onSelectClienteInRow={(rowIdx, c) => handleSelectClienteInRow(rowIdx, c, "bhe")}
              />
              {renderEmitButton(bheRows, "bhe", true, emitBhe, "Emitir BHE")}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BE Tab */}
        <TabsContent value="be" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Boleta Electrónica</CardTitle>
              <CardDescription>Receptor y detalle opcionales. El modo se infiere automáticamente según los campos que completes.</CardDescription>
            </CardHeader>
            <CardContent>
              {renderPreflight(true, [])}
              {renderBeModosInfo()}
              <EditableTable
                columns={beColumns}
                rows={beRows}
                onChange={setBeRows}
                rowStates={beStates}
                onRowStatesChange={setBeStates}
                emptyRow={emptyBe}
                validateRow={(r) => validateRowDetail(r, "be")}
                onOpenClienteSelector={() => openClienteSelectorForTab("be")}
                clientes={clientes}
                onSelectClienteInRow={(rowIdx, c) => handleSelectClienteInRow(rowIdx, c, "be")}
              />
              {renderEmitButton(beRows, "be", true, emitBe, "Emitir BE")}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Factura Tab */}
        <TabsContent value="factura" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Factura Electrónica</CardTitle>
              <CardDescription>
                Emisión masiva de facturas electrónicas exentas (DTE 34) o afectas con IVA (DTE 33).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderPreflight(facturaReady, facturaIssues)}

              {/* Barra superior de la tabla con Selector DTE e Instrucciones contextuales */}
              <div className="mb-3 rounded-lg border border-border/70 bg-muted/40 p-3 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
                {/* Lado izquierdo: Instrucción clara y contextual */}
                <div className="flex items-start gap-2.5">
                  <div className={`mt-0.5 p-1.5 rounded-md shrink-0 ${
                    facturaModo === "exenta"
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                  }`}>
                    {facturaModo === "exenta" ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">
                        {facturaModo === "exenta"
                          ? "Factura No Afecta o Exenta de IVA (DTE 34)"
                          : "Factura Afecta con 19% IVA (DTE 33)"}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                        facturaModo === "exenta"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-500/20"
                          : "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-500/20"
                      }`}>
                        {facturaModo === "exenta" ? "Sin IVA" : "IVA 19% Desglosado"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {facturaModo === "exenta"
                        ? "Ingresa el Monto Bruto Exento en la tabla. Válido para salud, educación, transporte de pasajeros y giros exentos."
                        : "Ingresa el Monto Total (con IVA incluido) en la tabla. El sistema calculará y desglosará automáticamente el Monto Neto y el IVA (19%) en el XML del SII."}
                    </p>
                  </div>
                </div>

                {/* Lado derecho: Selector segmentado con colores vibrantes sobre la esquina derecha de la tabla */}
                <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                  <span className="text-xs font-medium text-muted-foreground mr-1 hidden lg:inline">
                    Tipo de emisión:
                  </span>
                  <div className="flex items-center bg-background/90 p-1 rounded-lg border border-border shadow-xs">
                    <button
                      type="button"
                      onClick={() => setFacturaModo("exenta")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                        facturaModo === "exenta"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm ring-1 ring-emerald-500/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${facturaModo === "exenta" ? "bg-white animate-pulse" : "bg-emerald-500/50"}`} />
                      Exenta (34)
                    </button>
                    <button
                      type="button"
                      onClick={() => setFacturaModo("afecta")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                        facturaModo === "afecta"
                          ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm ring-1 ring-blue-500/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${facturaModo === "afecta" ? "bg-white animate-pulse" : "bg-blue-500/50"}`} />
                      Afecta con IVA (33)
                    </button>
                  </div>
                </div>
              </div>
              <EditableTable
                columns={computedFacturaColumns}
                rows={facturaRows}
                onChange={setFacturaRows}
                rowStates={facturaStates}
                onRowStatesChange={setFacturaStates}
                emptyRow={emptyFactura}
                validateRow={(r) => validateRowDetail(r, "factura")}
                onOpenClienteSelector={() => openClienteSelectorForTab("factura")}
                clientes={clientes}
                onSelectClienteInRow={(rowIdx, c) => handleSelectClienteInRow(rowIdx, c, "factura")}
              />
              {renderEmitButton(
                facturaRows,
                "factura",
                facturaReady,
                emitFactura,
                facturaModo === "exenta" ? "Emitir Facturas Exentas" : "Emitir Facturas Afectas"
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nota de Crédito Tab */}
        <TabsContent value="nota-credito" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Nota de Crédito (DTE 61)</CardTitle>
              <CardDescription>Anula o corrige facturas. Folio Ref = folio de la factura original.</CardDescription>
            </CardHeader>
            <CardContent>
              {renderPreflight(ncReady, facturaIssues)}
              <EditableTable
                columns={ncColumns}
                rows={ncRows}
                onChange={setNcRows}
                rowStates={ncStates}
                onRowStatesChange={setNcStates}
                emptyRow={emptyNc}
                validateRow={(r) => validateRowDetail(r, "nc")}
              />
              {renderEmitButton(ncRows, "nc", ncReady, emitNc, "Emitir Notas de Crédito")}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ClaveSiiModal
        open={claveModalOpen}
        onClose={() => { setClaveModalOpen(false); setPendingEmit(null); }}
        onSubmit={handleClaveSubmit}
        requiresCertificado={modalRequiresCert}
      />
      <EmisorSelectorModal
        open={emisorModalOpen}
        emisores={emisores}
        loading={emisoresLoading}
        onClose={() => { setEmisorModalOpen(false); setPendingBeEmit(null); }}
        onSelect={handleEmisorSelect}
      />
      <ConfirmEmitModal
        open={confirmModalOpen}
        summary={confirmSummary}
        onClose={() => {
          setConfirmModalOpen(false);
          setPendingConfirmAction(null);
        }}
        onConfirm={(options) => {
          setConfirmModalOpen(false);
          if (pendingConfirmAction) {
            const action = pendingConfirmAction;
            setPendingConfirmAction(null);
            action(options);
          }
        }}
      />
      <ClienteSelectorModal
        open={clienteSelectorOpen}
        clientes={clientes}
        currentRutEmisor={getActiveTabEmisor()}
        onClose={() => setClienteSelectorOpen(false)}
        onSelect={handleSelectClienteFromModal}
      />
    </div>
  );
}
